import Spinner from '@medflex/components/stories/Spinner';
import useMatomoTracker from '@medflex/hooks/useMatomoTracker';
import useUserSession from '@medflex/hooks/useUserSession';
import isAnySessionChatId from '@medflex/utils/isAnySessionChatId';
import isDesktop from '@medflex/utils/isDesktop';
import isMobileDeviceOrApp from '@medflex/utils/isMobileDeviceOrApp';
import { FEATURE_KEYS, SUBSCRIPTION_PLANS } from '@medflex/utils/subscriptionEnums';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ToastContainer } from '@medflex/components/stories/Toast';
import useScreenSize, { BREAKPOINTS } from '@medflex/hooks/useScreenSize';
import Media from '@medflex/components/stories/Media/Media';
import { Trans, useTranslation } from 'react-i18next';
import Link from '@medflex/components/stories/Link';
import Tooltip from '@medflex/components/stories/Tooltip';
import AbsenceInfoBanner from '@medflex/components/stories/AbsenceInfoBanner';
import { PATHS_FOR_SIMPLIFIED_UI } from '../constants';
import ErrorPage from '../components/ErrorPage';
import DesktopBadgeCount from '../components/DesktopBadgeCount';
import useAddressBookContacts from '../hooks/useAddressBookContacts';
import useChatConnection from '../hooks/useChatConnection';
import useChatContacts from '../hooks/useChatContacts';
import useFeatures from '../hooks/useFeatures';
import useInvitationRequests from '../hooks/useInvitationRequests';
import useMessages from '../hooks/useMessages';
import useOnlineStatus from '../hooks/useOnlineStatus';
import useSelectedContactId from '../hooks/useSelectedContactId';
import useSessionMeetings from '../hooks/useSessionMeetings';
import useStaff from '../hooks/useStaff';
import useUserProfile from '../hooks/useUserProfile';
import useWindowTitleNotification from '../hooks/useWindowTitleNotification';
import AppSettingsModule from '../modules/AppSettingsModule/AppSettingsModule';
import CallReportModule from '../modules/CallReportModule';
import ChatListModule from '../modules/ChatListModule';
import DeleteMessageModule from '../modules/DeleteMessageModule';
import IncomingCallModule from '../modules/IncomingCallModule';
import InvitePatientsModalModule from '../modules/InvitePatientModalModule';
import NavBarModule from '../modules/NavBarModule';
import NotificationsModule from '../modules/NotificationsModule';
import PhysicianAddressBookModule from '../modules/PhysicianAddressBookModule';
import PhysicianAdministrationModule from '../modules/PhysicianAdministrationModule/PhysicianAdministrationModule';
import PhysicianChatRoomModule from '../modules/PhysicianChatRoomModule';
import PhysicianCodeSnippetsModule from '../modules/PhysicianCodeSnippetsModule';
import PhysicianContactConnectorModule from '../modules/PhysicianContactConnectorModule';
import PhysicianInvitationCodeModule from '../modules/PhysicianInvitationCodeModule';
import PhysicianInvitePhysicianModal from '../modules/PhysicianInvitePhysicianModule';
import PhysicianModalsModule from '../modules/PhysicianModalsModule';
import PhysicianOrganizationJoinModule from '../modules/PhysicianOrganizationJoinModule';
import PhysicianSessionChatJoinModule from '../modules/PhysicianSessionChatJoinModule';
import PhysicianTitleBarModule from '../modules/PhysicianTitleBarModule';
import PhysicianWelcomeScreenModule from '../modules/PhysicianWelcomeScreenModule/PhysicianWelcomeScreenModule';
import SessionChatModule from '../modules/SessionChatModule';
import SessionNotificationModule from '../modules/SessionNotificationModule';
import SessionsListModule from '../modules/SessionsListModule';
import InvitePatientRedirectRoute from '../routes/InvitePatientRedirectRoute';
import useSubscription from '../hooks/useSubscription';
import useSessionChats from '../hooks/useSessionChats';
import useSnippets from '../hooks/useSnippets';
import VideoMenuModule from '../modules/VideoMenuModule';
import ContactOverviewModule from '../modules/ContactOverviewModule/ContactOverviewModule';
import ActivitiesModule from '../modules/ActivitiesModule/ActivitiesModule';
import InvitationRequestsModule from '../modules/InvitationRequestsModule';
import ContactOverviewTitleBar from '../modules/ContactOverviewModule/ContactOverviewTitleBar';
import useInboxSearchQuery from '../hooks/useInboxSearchQuery';

// eslint-disable-next-line import/no-unresolved
const RemoteInboxApp = React.lazy(() => import('RemoteInbox/App'));

const PhysicianApp = () => {
  const { t } = useTranslation('messenger');
  const { allPatients } = useAddressBookContacts(true);
  const { contacts } = useChatContacts(true);
  const contactId = useSelectedContactId();
  const { find } = useMessages();
  const { initiatePulling, onlineUserIds } = useOnlineStatus();
  const { load: loadSessionMeetings } = useSessionMeetings();
  const { load: loadSessionChats } = useSessionChats();
  const {
    plan: subscriptionPlan,
    email: subscriptionEmail,
    contractStatus,
    trialEndedAt,
  } = useSubscription();
  const [chatCharacters, pamFeature, pamPhoneAssistant] = useFeatures([
    FEATURE_KEYS.CHAT_CHARACTERS,
    FEATURE_KEYS.PAM,
    FEATURE_KEYS.PAM_PHONE_ASSISTANT,
  ]);
  const { userProfile } = useUserProfile();
  const inboxSearchQuery = useInboxSearchQuery();

  const { session: userSession } = useUserSession();
  const { quickReplySnippets } = useSnippets(true);
  const inquiryManagementDefaultAbsenceSettings =
    userProfile?.attributes?.pages?.inquiryManagementSettings?.defaultFunnel?.absence;

  const { width } = useScreenSize();
  const isExtraLargeScreen = width >= BREAKPOINTS.XL;

  useChatConnection();
  useWindowTitleNotification();
  useInvitationRequests(true);
  useStaff(true);
  useMatomoTracker();

  useEffect(() => {
    if (contacts.length > 0 && contactId) {
      find({ contactId });
    }
  }, [contacts.length, contactId]);

  useEffect(() => {
    initiatePulling();
    loadSessionMeetings();
    loadSessionChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscriptionMemo = useMemo(() => {
    return {
      plan: subscriptionPlan,
      email: subscriptionEmail,
      phoneAssistant: pamPhoneAssistant,
      contractStatus,
      trialEndedAt,
      ...pamFeature,
    };
  }, [
    pamFeature,
    pamPhoneAssistant,
    subscriptionEmail,
    subscriptionPlan,
    contractStatus,
    trialEndedAt,
  ]);

  return (
    <div className="relative flex h-full min-h-0 flex-col" data-component="PhysicianApp">
      {![SUBSCRIPTION_PLANS.BASIC, SUBSCRIPTION_PLANS.STARTERSESSION].includes(subscriptionPlan) &&
        inquiryManagementDefaultAbsenceSettings && (
          <AbsenceInfoBanner
            startAt={inquiryManagementDefaultAbsenceSettings.startAt}
            endAt={inquiryManagementDefaultAbsenceSettings.endAt}
            className="justify-center p-2"
          >
            <Trans i18nKey="physician:InquiryManagementModule.Absence.banner.text">
              <Link to="/settings/inquiryManagement#absence" className="!text-amber-900 underline">
                Settings
              </Link>
            </Trans>
          </AbsenceInfoBanner>
        )}
      {isDesktop() ? (
        <>
          <DesktopBadgeCount />
          <NotificationsModule />
        </>
      ) : (
        <Media minWidth={BREAKPOINTS.LG}>
          <NotificationsModule />
        </Media>
      )}
      <Switch>
        <Route
          exact
          path="/report/:id"
          render={({ match }) => <CallReportModule id={match.params.id} />}
        />
        <Route
          path="/verbinden/:permanentInvitationId?"
          render={({ match }) => (
            <PhysicianInvitationCodeModule
              permanentInvitationId={match.params.permanentInvitationId}
            />
          )}
        />
        <Route
          path="/org/beitreten/:permanentJoinId"
          render={({ match }) => (
            <PhysicianOrganizationJoinModule permanentJoinId={match.params.permanentJoinId} />
          )}
        />
        <Route path="/einladen" component={InvitePatientRedirectRoute} />
        <Route path="/snippets" component={PhysicianCodeSnippetsModule} />
        <Route>
          <Route path={PATHS_FOR_SIMPLIFIED_UI}>
            {/* hide while contact selected; using hidden so the component will stay mounted */}
            {routeProps => (
              <PhysicianTitleBarModule
                className={`${!(isExtraLargeScreen || !routeProps.match) ? 'hidden' : ''}`}
              />
            )}
          </Route>

          <div className="flex-auto h-full min-h-0">
            <div
              className={`flex flex-col-reverse h-full min-h-0 relative w-screen ${
                isMobileDeviceOrApp() ? '' : 'sm:flex-row'
              }`}
            >
              <Switch>
                <Route path={PATHS_FOR_SIMPLIFIED_UI}>
                  <Media minWidth={BREAKPOINTS.XS}>
                    <NavBarModule />
                  </Media>
                </Route>
                <Route component={NavBarModule} />
              </Switch>

              <Switch>
                <Route path="/inbox/requests">
                  <Route
                    render={({ location }) => {
                      const { search, pathname, hash } = location;
                      if (!search && inboxSearchQuery) {
                        return (
                          <Redirect
                            to={{
                              pathname,
                              search: inboxSearchQuery,
                              hash,
                            }}
                          />
                        );
                      }
                      return null;
                    }}
                  />
                  <Route
                    render={() => (
                      <Suspense
                        fallback={
                          <div className="flex w-full items-center justify-center">
                            <Spinner size="3xl" />
                          </div>
                        }
                      >
                        <ErrorPage className="w-full" userType={userProfile?.type}>
                          <div className="flex h-full w-full flex-col overflow-y-auto">
                            <RemoteInboxApp
                              contacts={allPatients}
                              jwt={userSession?.access_token}
                              userProfile={userProfile}
                              subscription={subscriptionMemo}
                              onlineUserIds={onlineUserIds}
                              snippets={quickReplySnippets}
                              renderVideoMenu={({ id, canCall }) => (
                                <Tooltip title={t('VideoMenuModule.tooltip_title')}>
                                  <div>
                                    <VideoMenuModule
                                      id={id}
                                      canCall={canCall}
                                      className="text-slate-600"
                                    />
                                  </div>
                                </Tooltip>
                              )}
                              renderChat={({ id }) => {
                                return (
                                  <SessionChatModule id={id} chatCharacters={chatCharacters} />
                                );
                              }}
                            />
                          </div>
                        </ErrorPage>
                      </Suspense>
                    )}
                  />
                </Route>
                <Route path="/sessions" component={SessionsListModule} />
                <Route path="/settings" component={PhysicianAdministrationModule} />
                <Route path="/invitation-requests" component={InvitationRequestsModule} />
                <Route path="/app-settings" component={AppSettingsModule} />
                <Route path="/address-book/:contactId?">
                  <Switch>
                    <Route
                      path="/address-book/:contactId/overview"
                      render={() => (
                        <>
                          <Media minWidth={BREAKPOINTS.XL}>
                            <PhysicianAddressBookModule />
                          </Media>
                          <div className="flex flex-col w-full h-full overflow-auto">
                            <div className="px-4 xl:px-8 border-b-1">
                              <ContactOverviewTitleBar />
                            </div>
                            <div className="overflow-auto py-4">
                              <div className="max-w-5xl mx-auto px-2 xl:px-8 w-full">
                                <ContactOverviewModule />
                                <Suspense
                                  fallback={
                                    <div className="flex w-full items-center justify-center">
                                      <Spinner size="sm" />
                                    </div>
                                  }
                                >
                                  <ErrorPage className="w-full" userType={userProfile?.type}>
                                    <ActivitiesModule />
                                  </ErrorPage>
                                </Suspense>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    />
                    <Route
                      path="/address-book/:userId"
                      component={PhysicianContactConnectorModule}
                    />
                    <Route component={PhysicianAddressBookModule} />
                  </Switch>
                </Route>
                <Route path="/address-book" component={PhysicianAddressBookModule} />
                <Route path="/chat/:id?">
                  <Switch>
                    <Route
                      path="/chat/:id"
                      render={({ match }) => (
                        <Media minWidth={BREAKPOINTS.XL}>
                          <ChatListModule selectedChatId={match.params.id} />
                        </Media>
                      )}
                    />
                    <Route component={ChatListModule} />
                  </Switch>
                  <Switch>
                    <Route
                      path="/chat/:sessionId/beitreten/:permanentJoinId"
                      render={({ match }) => (
                        <PhysicianSessionChatJoinModule
                          id={match.params.sessionId}
                          permanentJoinId={match.params.permanentJoinId}
                        />
                      )}
                    />
                    <Route
                      path="/chat/:id"
                      render={({ match, ...props }) => {
                        const { params } = match;
                        if (isAnySessionChatId(params.id)) {
                          return (
                            <SessionChatModule
                              key={`sessionChat_${params.id}`}
                              id={params.id}
                              chatCharacters={chatCharacters}
                            />
                          );
                        }
                        return <PhysicianChatRoomModule {...props} />;
                      }}
                    />
                  </Switch>
                </Route>
                <Route component={PhysicianWelcomeScreenModule} />
              </Switch>
            </div>
          </div>
        </Route>
      </Switch>
      <IncomingCallModule />
      <ToastContainer />
      <DeleteMessageModule />
      <InvitePatientsModalModule />
      <Route path="/email-einladung" component={PhysicianInvitePhysicianModal} />
      <PhysicianModalsModule />
      <SessionNotificationModule />
    </div>
  );
};

PhysicianApp.propTypes = {};

export default PhysicianApp;
